// src/api.js
// Shared API base URL + session-token handling for the website.
// Every fetch to a /api/tracker/:username route needs the bearer token
// attached — use apiFetch instead of raw fetch for those calls.

/**
 * THE PRODUCTION DOMAIN. The API answers on api.<domain>, this site on <domain>.
 *
 * Spelled out here rather than read from a .env file because .env.* is
 * gitignored: a build machine that lacks the file would silently produce a
 * bundle pointing at localhost, and the only symptom is every request failing
 * once it is already deployed. A constant cannot go missing.
 *
 * The extension pins the same domain in extension/src/config.js and
 * extension/manifest.prod.json. All three must agree.
 */
const PROD_DOMAIN = 'skinsoar.com';

/**
 * Where the account API lives.
 *
 * `npm start` sets NODE_ENV to 'development' and `npm run build` sets it to
 * 'production', so the dev server keeps talking to a local API without anyone
 * having to remember to switch it back before committing.
 *
 * REACT_APP_API_ORIGIN overrides both, for pointing a build at a staging API.
 * Create React App inlines it at build time; it is not read at runtime.
 */
export const API =
  process.env.REACT_APP_API_ORIGIN ||
  (process.env.NODE_ENV === 'production'
    ? `https://api.${PROD_DOMAIN}`
    : 'http://localhost:3001');

const TOKEN_KEY = 'cs2pt_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Drop-in replacement for fetch() against the API — prepends API and attaches
// the Authorization header when a session token is present.
export function apiFetch(path, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API}${path}`, { ...options, headers });
}

// ── Signed-in username ───────────────────────────────────────
// Kept under its own key for historical reasons — the app read `user` long
// before there was a token to go with it. The two are one credential though, so
// clearSession() below is what everything should call to sign out.
const USER_KEY = 'user';

export function getUser() {
  return localStorage.getItem(USER_KEY);
}

export function setUser(username) {
  localStorage.setItem(USER_KEY, username);
}

export function clearSession() {
  clearToken();
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ACCOUNT_KEY);
}

// ── Cached account summary ───────────────────────────────────
// The Steam-link gate needs to know whether this account has a Steam ID before
// it can decide whether to let the app render. Asking the server first would
// put a round trip in front of every load. Caching the last known answer lets
// the app paint immediately and revalidate behind it — the cache can only be
// wrong for a moment, and the guard corrects it when the truth arrives.
const ACCOUNT_KEY = 'cs2pt_account';

export function getCachedAccount() {
  try {
    return JSON.parse(localStorage.getItem(ACCOUNT_KEY) || 'null');
  } catch {
    return null;
  }
}

export function setCachedAccount(account) {
  if (account) localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  else localStorage.removeItem(ACCOUNT_KEY);
}

// ── Sign in with Steam ───────────────────────────────────────
//
// A plain top-level navigation, never fetch and never an iframe: Steam serves
// its own login page and sends X-Frame-Options, and an XHR cannot follow the
// user through a Steam Guard prompt.
//
// `redirect` tells the API which of its allowlisted origins to send the browser
// back to, so www vs apex vs a dev server all return where they started.

/** Start a fresh Steam sign-in. Comes back to /signin with a session token. */
export function steamLoginUrl() {
  return `${API}/api/steam-auth/login?redirect=${encodeURIComponent(window.location.origin)}`;
}

/**
 * Start a Steam link for the account already signed in on this tab.
 *
 * Two steps because the navigation cannot carry an Authorization header: swap
 * the session token for a single-use ticket first, so the long-lived credential
 * never appears in a URL. Returns the URL to navigate to.
 */
export async function steamLinkUrl() {
  const res = await apiFetch('/api/steam-auth/link-ticket', { method: 'POST' });
  if (!res.ok) throw new Error('Could not start Steam linking');
  const { ticket } = await res.json();
  return `${API}/api/steam-auth/login`
    + `?redirect=${encodeURIComponent(window.location.origin)}`
    + `&ticket=${encodeURIComponent(ticket)}`;
}

/**
 * Read the outcome the API left in the URL fragment, and scrub it.
 *
 * The fragment rather than the query string is deliberate on the server side —
 * it keeps the session token out of the frontend host's access logs. Scrubbing
 * with replaceState keeps it out of history, bookmarks and Referer too.
 *
 * @returns {Record<string,string>} empty when there was nothing to read
 */
export function consumeAuthFragment() {
  const raw = window.location.hash.replace(/^#/, '');
  if (!raw) return {};
  const parsed = Object.fromEntries(new URLSearchParams(raw).entries());
  // Only claim fragments that are ours; leave anchors and hash routes alone.
  if (!('token' in parsed) && !('error' in parsed) && !('linked' in parsed)) return {};
  window.history.replaceState({}, '', window.location.pathname + window.location.search);
  return parsed;
}

/**
 * The result of a Steam link, which the API drops on /app.
 *
 * Captured once here, at module load, rather than by whichever component
 * happens to want it. Two reasons, both of which have teeth:
 *
 *   - consumeAuthFragment scrubs what it reads, and there are two readers now.
 *     The path guard is what stops this one swallowing the sign-in token meant
 *     for AuthPage on /signin.
 *   - the Inventory tab's panel only mounts once that tab is open, which is too
 *     late to read a fragment. Capturing it here means the value still exists
 *     when the tab opens to show it.
 *
 * A link return is always a full page load, so module scope outlives it.
 */
export const STEAM_LINK_RESULT =
  window.location.pathname === '/app' ? consumeAuthFragment() : {};

/**
 * The account behind the current session, or null if the session is not valid.
 *
 * A null answer is load-bearing — callers sign the user out on it — so a
 * network failure must not produce one. Only an actual rejection from the
 * server counts as "not signed in"; anything else rethrows.
 */
export async function fetchAccount() {
  const res = await apiFetch('/api/tracker/me');
  if (res.status === 401 || res.status === 403 || res.status === 404) {
    setCachedAccount(null);
    return null;
  }
  if (!res.ok) throw new Error(`/me failed: ${res.status}`);
  const data = await res.json();
  setCachedAccount(data.account || null);
  return data.account || null;
}
